import { clearFilterParams } from '@btime/react-contexts'
import { gql } from '@apollo/client'
import { pathOr } from 'ramda'
import { useCustomToken } from '@btime/react-session'
import React, { useEffect, useCallback, useRef, useState } from 'react'
import { useNotification } from '@btime/react-messager'
import { useTranslation } from 'react-i18next'

import { placesClient } from '../../../utils/apollo/api'
import { usePromiseQuery } from '../../../utils/apollo/_helpers/promiseQuery'
import Select from '../../../packages/kiti/components/form/Select'
import SelectWithInterpolation from '../../../packages/kiti-editor/components/select/SelectWithInterpolation'
import { formatSelectedLabel, useSelectResetKey } from '../../../utils/helpers/select'
import { cleanWhenEnabledFilterIsSet } from '../../../modules/_helpers/filterByEnabled'

const SELECT_ALL_ASSETS_LIMIT = 100

const QUERY = gql`
  query Assets($search: String, $page: Int, $filter: AssetFilter, $formToken: String, $limit: Int) {
    assets(
      search: $search
      page: $page
      filter: $filter
      formToken: $formToken
      limit: $limit
      sort: { field: NAME, type: ASC }
    ) {
      id
      name
      placeId
      place {
        id
        name
      }
      type {
        id
        name
      }
      fieldValues {
        id
        value
        field {
          id
          name
          type
        }
        __typename
      }
    }
  }
`

export const useAssetsLoader = ({ filter }) => {
  filter = cleanWhenEnabledFilterIsSet({ ...filter })
  const formToken = useCustomToken('formToken')
  const query = usePromiseQuery(QUERY, { client: placesClient, fetchPolicy: 'cache-first' })

  return (search, page = 1, limit) => {
    return query({
      search,
      page,
      formToken,
      filter: clearFilterParams(filter || {}),
      limit
    })
      ?.then(pathOr([], ['data', 'assets']))
      ?.then(assets =>
        assets.map(asset => ({
          ...asset,
          _typeAndPlace: [asset?.type?.name, asset?.place?.name].filter(Boolean).join(' - ')
        }))
      )
  }
}

const AssetSelect = ({ onChange, value, multiple, interpolations = [], ...props }) => {
  const getOptions = useAssetsLoader(props)
  const notify = useNotification()
  const { t } = useTranslation('assets')
  const isSelectingAll = useRef(false)
  const { key } = useSelectResetKey(value)
  const [allAssetsSelected, setAllAssetsSelected] = useState(false)

  const fetchAllAssets = useCallback(async () => {
    let allAssets = []
    let page = 1
    let hasMore = true

    try {
      while (hasMore) {
        const assets = await getOptions('', page, SELECT_ALL_ASSETS_LIMIT)

        if (assets.length === 0) {
          hasMore = false
        } else {
          allAssets = [...allAssets, ...assets]
          page++
        }
      }

      return allAssets
    } catch (error) {
      notify.error({
        title: t('errors.loadAll'),
        message: t('errors.loadAllMessage')
      })
      return []
    }
  }, [getOptions, notify, t])

  const checkIfAllSelected = useCallback(() => {
    if (Array.isArray(value) && value.length === 1 && value[0]?.id === 'select-all') {
      return
    }

    if (!Array.isArray(value) || value.length === 0) {
      setAllAssetsSelected(false)
      return
    }

    if (isSelectingAll.current) {
      fetchAllAssets().then(allAssets => {
        const isAllSelected =
          allAssets.length > 0 &&
          allAssets.length === value.length &&
          allAssets.every(asset => value.some(v => v.id === asset.id))

        setAllAssetsSelected(isAllSelected)
      })
    } else {
      setAllAssetsSelected(false)
    }
  }, [value, fetchAllAssets])

  useEffect(() => {
    if (isSelectingAll.current) {
      checkIfAllSelected()
    }
  }, [value, checkIfAllSelected])

  const handleSelectAll = async () => {
    isSelectingAll.current = true
    const allAssets = await fetchAllAssets()
    onChange(allAssets)
    setAllAssetsSelected(true)
    isSelectingAll.current = false
  }

  const handleChange = selectedValue => {
    const isSelectAll =
      (Array.isArray(selectedValue) && selectedValue.some(item => item.id === 'select-all')) ||
      (!Array.isArray(selectedValue) && selectedValue?.id === 'select-all') ||
      (Array.isArray(value) &&
        value.some(item => item.id === 'select-all') &&
        Array.isArray(selectedValue) &&
        !selectedValue.some(item => item.id === 'select-all'))

    if (isSelectAll) {
      handleSelectAll()
      return
    }

    if (Array.isArray(selectedValue) && selectedValue.length === 0) {
      setAllAssetsSelected(false)
      onChange(selectedValue)
      return
    } else if (Array.isArray(selectedValue) && Array.isArray(value) && selectedValue.length < value.length) {
      setAllAssetsSelected(false)
    }

    onChange(selectedValue)
  }

  const isMultiple = multiple === true || Array.isArray(value)

  const Component = !!interpolations.length ? SelectWithInterpolation : Select

  const loadAssets = useCallback(
    async (search, page = 1) => {
      try {
        const assets = await getOptions(search, page)
        return assets
      } catch (error) {
        notify.error({
          title: t('errors.load'),
          message: t('errors.loadMessage')
        })
        return []
      }
    },
    [getOptions, notify, t]
  )

  return (
    <Component
      key={key}
      value={value}
      onChange={handleChange}
      options={loadAssets}
      descriptionField="_typeAndPlace"
      useEmptyOption={true}
      multiple={isMultiple}
      selectAll={false}
      selectedLabel={formatSelectedLabel(value)}
      hasSelectAll={isMultiple && !allAssetsSelected}
      onSelectAll={handleSelectAll}
      interpolations={interpolations}
      origin="asset"
      {...props}
    />
  )
}

export default AssetSelect
